<template>
  <div>
    <channel-outcomes-dialog
      v-model="showOutcomesDialog"
      :channel="outcomesChannel"
    ></channel-outcomes-dialog>
    <v-row class="align-center">
      <v-col>
        <h1 class="outline--text">Parsing overview</h1>
      </v-col>
      <v-col cols="12" sm="4" md="2">
        <v-select
          v-model="days"
          :items="periods"
          item-text="label"
          item-value="value"
          label="Period"
          dense
          @change="fetchAll"
        ></v-select>
      </v-col>
      <v-col cols="auto">
        <v-btn icon :loading="isLoading" @click="fetchAll">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-alert text color="red" type="error" v-if="errorMsg">
      {{ errorMsg }}
    </v-alert>
    <v-progress-linear
      v-if="isLoading && !overview"
      indeterminate
    ></v-progress-linear>

    <v-row v-if="overview">
      <v-col cols="12" lg="4">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">Download methods</v-card-title>
          <v-card-text class="black--text">
            <div>
              Autoswitch:
              <v-chip x-small outlined :color="modeColor">{{
                overview.autoswitchMode
              }}</v-chip>
            </div>
            <div class="mb-2">
              Automatic Playwright channels: {{ autoPlaywright }} /
              {{ overview.maxAutoPlaywright }}
            </div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Chosen</th>
                  <th class="text-right">Channels</th>
                  <th class="text-right">Http probe due</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in overview.methods"
                  :key="`${item.downloadMethod}-${item.mode}`"
                >
                  <td>{{ item.downloadMethodName }}</td>
                  <td class="grey--text">{{ modeLabels[item.mode] }}</td>
                  <td class="text-right">{{ item.channels }}</td>
                  <td class="text-right">
                    <span v-if="item.probeDue">{{ item.probeDue }}</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <p class="grey--text text-caption mt-2 mb-0">
              Active website, subsite and rss channels. Http probes run only
              with autoswitch "on".
            </p>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="8">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">
            Outcomes of all channels
          </v-card-title>
          <v-card-text>
            <outcomes-tables :outcomes="overview" with-share></outcomes-tables>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="overview" outlined class="mt-4">
      <v-card-title class="text-subtitle-1">Channels</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="view"
              :items="views"
              item-text="label"
              item-value="key"
              label="Show"
              dense
              @change="onViewChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="outcome"
              :items="outcomeOptions"
              label="Outcome"
              dense
              clearable
              placeholder="Any"
              @change="fetchChannels"
            ></v-select>
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <v-select
              v-model="limit"
              :items="[50, 100, 500]"
              label="Top"
              dense
              @change="fetchChannels"
            ></v-select>
          </v-col>
        </v-row>
        <v-data-table
          :headers="channelHeaders"
          :items="channels"
          :loading="isChannelsLoading"
          item-key="channelId"
          :items-per-page="50"
          :footer-props="{ 'items-per-page-options': [50, 100, -1] }"
          dense
          class="elevation-0"
        >
          <template v-slot:item.channelName="{ item }">
            <a
              target="_blank"
              :href="item.url"
              :class="item.isActive ? 'black--text' : 'grey--text'"
              >{{ item.channelName || item.channelId }}</a
            >
            <span class="grey--text ml-1">#{{ item.channelId }}</span>
            <span v-if="!item.isActive" class="grey--text ml-1">(off)</span>
          </template>
          <template v-slot:item.sourceName="{ item }">
            <router-link v-if="item.sourceId" :to="sourceRoute(item)">{{
              item.sourceName || item.sourceId
            }}</router-link>
            <span v-if="item.isCustom" class="grey--text ml-1">(custom)</span>
          </template>
          <template v-slot:item.downloadMethodName="{ item }">
            <span class="text-no-wrap">
              {{ item.downloadMethodName }}
              <v-icon v-if="item.downloadMethodPinned" x-small
                >mdi-pin-outline</v-icon
              >
            </span>
          </template>
          <template v-slot:item.share="{ item }">
            <span v-if="item.share !== null">{{ item.share }}%</span>
          </template>
          <template v-slot:item.outcomes="{ item }">
            <outcome-chips :items="item.outcomes"></outcome-chips>
          </template>
          <template v-slot:item.lastDate="{ item }">
            <span class="grey--text text-no-wrap">{{ item.lastDate }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              depressed
              rounded
              x-small
              icon
              color="grey darken-1"
              @click="showOutcomes(item)"
            >
              <v-icon> mdi-chart-box-outline </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  FETCH_PARSING_CHANNELS,
  FETCH_PARSING_OVERVIEW,
} from "@/store/modules/channel/actions";
import ChannelOutcomesDialog from "@/views/channel/item/ChannelOutcomesDialog";
import OutcomeChips from "@/views/channel/item/OutcomeChips";
import OutcomesTables from "@/views/channel/item/OutcomesTables";

const VIEWS = [
  { key: "articles", label: "Article problems", jobType: "articles" },
  {
    key: "articles-fetch",
    label: "Article fetch problems",
    jobType: "articles",
    stage: "fetch",
  },
  {
    key: "articles-parse",
    label: "Article parse problems",
    jobType: "articles",
    stage: "parse",
  },
  { key: "links", label: "Links problems", jobType: "links" },
  {
    key: "links-fetch",
    label: "Links fetch problems",
    jobType: "links",
    stage: "fetch",
  },
  {
    key: "links-parse",
    label: "Links parse problems",
    jobType: "links",
    stage: "parse",
  },
  { key: "method", label: "Download method decisions", stage: "method" },
];

export default {
  name: "ParsingOverviewPage",
  components: { ChannelOutcomesDialog, OutcomeChips, OutcomesTables },
  data() {
    return {
      days: 7,
      periods: [
        { label: "Today", value: 1 },
        { label: "7 days", value: 7 },
        { label: "14 days", value: 14 },
        { label: "30 days", value: 30 },
        { label: "90 days", value: 90 },
      ],
      modeLabels: {
        auto: "automatically",
        pinned: "by hand",
        fixed: "fixed (not pinned)",
      },
      views: VIEWS,
      view: "articles",
      outcome: null,
      limit: 50,
      overview: null,
      channels: [],
      isLoading: false,
      isChannelsLoading: false,
      errorMsg: "",
      showOutcomesDialog: false,
      outcomesChannel: null,
    };
  },
  computed: {
    selectedView() {
      return VIEWS.find((view) => view.key === this.view) || VIEWS[0];
    },
    isMethodView() {
      return this.selectedView.stage === "method";
    },
    // outcomes seen in the period for the selected view
    outcomeOptions() {
      if (!this.overview) {
        return [];
      }
      const { totals } = this.overview;
      const { jobType, stage } = this.selectedView;
      let items;
      if (this.isMethodView) {
        items = totals.method;
      } else if (stage) {
        items = totals[jobType][stage];
      } else {
        items = totals[jobType].fetch.concat(totals[jobType].parse);
      }
      return items
        .filter((item) => item.kind !== "ok")
        .map((item) => item.outcome);
    },
    channelHeaders() {
      const headers = [
        { text: "Channel", value: "channelName" },
        { text: "Source", value: "sourceName" },
        { text: "Method", value: "downloadMethodName" },
        {
          text: this.isMethodView ? "Decisions" : "Problems",
          value: "problems",
          align: "end",
        },
      ];
      if (!this.isMethodView) {
        headers.push(
          { text: "Attempts", value: "attempts", align: "end" },
          { text: "Share", value: "share", align: "end" }
        );
      }
      return headers.concat([
        { text: "Outcomes", value: "outcomes", sortable: false },
        { text: "Last", value: "lastDate" },
        { text: "", value: "actions", sortable: false, width: "40" },
      ]);
    },
    autoPlaywright() {
      const item = (this.overview.methods || []).find(
        (method) => method.downloadMethod === 2 && method.mode === "auto"
      );
      return item ? item.channels : 0;
    },
    modeColor() {
      const colors = { on: "green", shadow: "orange" };
      return colors[this.overview.autoswitchMode] || "grey";
    },
  },
  methods: {
    fetchAll() {
      this.fetchOverview();
      this.fetchChannels();
    },
    fetchOverview() {
      const vm = this;
      vm.isLoading = true;
      vm.errorMsg = "";
      vm.$store
        .dispatch(`channels/${FETCH_PARSING_OVERVIEW}`, { days: vm.days })
        .then(
          (data) => {
            vm.overview = data.data;
            vm.isLoading = false;
          },
          (error) => {
            vm.errorMsg = error;
            vm.isLoading = false;
          }
        );
    },
    fetchChannels() {
      const vm = this;
      const { jobType, stage } = vm.selectedView;
      const params = { days: vm.days, limit: vm.limit };
      if (jobType) {
        params.jobType = jobType;
      }
      if (stage) {
        params.stage = stage;
      }
      if (vm.outcome) {
        params.outcome = vm.outcome;
      }
      vm.isChannelsLoading = true;
      vm.$store.dispatch(`channels/${FETCH_PARSING_CHANNELS}`, params).then(
        (data) => {
          vm.channels = data.data;
          vm.isChannelsLoading = false;
        },
        (error) => {
          vm.errorMsg = error;
          vm.isChannelsLoading = false;
        }
      );
    },
    onViewChange() {
      this.outcome = null;
      this.fetchChannels();
    },
    sourceRoute(item) {
      return {
        name: item.isCustom ? "CustomSourceItemPage" : "SourceItemPage",
        params: { id: item.sourceId },
      };
    },
    showOutcomes(item) {
      this.outcomesChannel = { id: item.channelId, name: item.channelName };
      this.showOutcomesDialog = true;
    },
  },
  mounted() {
    this.fetchAll();
  },
};
</script>
